import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Upload, Download, Trophy, Flame } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Section } from '../FormFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VoucherCodesPanel = () => {
  const [vouchers, setVouchers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', label: '', agent_username: '', expires_at: '' });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const isExpired = (v) => v.expires_at && v.expires_at < new Date().toISOString().slice(0, 10);

  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [vRes, tRes, aRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/vouchers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/team-accounts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/vouchers/analytics`, { withCredentials: true }),
      ]);
      setVouchers(vRes.data.vouchers || []);
      setAgents((tRes.data.members || []).filter((m) => m.role === 'agent' || m.role === 'manager'));
      setAnalytics(aRes.data.by_agent || []);
    } catch (err) {
      if (!silent) toast.error('Failed to load voucher codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // Agent/manager roster (used for the "assign to" dropdown) can change
    // from Team Accounts — poll silently so it never goes stale in-session.
    const interval = setInterval(() => fetchAll(true), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.label.trim() || !form.agent_username || saving) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/vouchers`, {
        code: form.code.trim(), label: form.label.trim(), agent_username: form.agent_username,
        expires_at: form.expires_at || null,
      }, { withCredentials: true });
      toast.success('Voucher code created');
      setForm({ code: '', label: '', agent_username: '', expires_at: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create voucher code');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (voucher) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/vouchers/${voucher.id}`, { active: !voucher.active }, { withCredentials: true });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update voucher code');
    }
  };

  const handleDelete = async (voucherId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/vouchers/${voucherId}`, { withCredentials: true });
      toast.success('Voucher code deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete voucher code');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/vouchers/bulk-import`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (res.data.created_count > 0) toast.success(`Imported ${res.data.created_count} voucher code(s)`);
      if (res.data.error_count > 0) toast.error(`${res.data.error_count} row(s) skipped — see details below`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/vouchers/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `voucher-codes-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export voucher codes');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-400 italic">Loading voucher codes…</p>;

  return (
    <Section
      title="Voucher Codes"
      description="Agent-identifier referral codes. A visitor entering a valid, unredeemed code on the public contact form is auto-assigned straight to its linked Agent — used together with a region's 'Voucher required' toggle above."
    >
      <div className="space-y-4" data-testid="voucher-codes-panel">
        <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex-1 min-w-[120px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Code</label>
            <input
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="JANE-REF"
              data-testid="voucher-code-input"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Label / nickname</label>
            <input
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="Jane's referral code"
              data-testid="voucher-label-input"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Linked Agent/Manager</label>
            <select
              value={form.agent_username}
              onChange={(e) => setForm((f) => ({ ...f, agent_username: e.target.value }))}
              data-testid="voucher-agent-select"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Select…</option>
              {agents.map((a) => <option key={a.username} value={a.username}>{`${a.name} (${a.role})`}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Expires (optional)</label>
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
              data-testid="voucher-expires-input"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !form.code.trim() || !form.label.trim() || !form.agent_username}
            data-testid="voucher-create-button"
            className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportFile}
            className="hidden"
            data-testid="voucher-import-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="voucher-import-button"
            className="inline-flex items-center gap-1.5 bg-white border-2 border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
            title="Import codes from a CSV (columns: code, label, agent_username, expires_at)"
          >
            <Upload className="w-4 h-4" /> {importing ? 'Importing…' : 'Import CSV'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || vouchers.length === 0}
            data-testid="voucher-export-button"
            className="inline-flex items-center gap-1.5 bg-white border-2 border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
            title="Download all codes as CSV"
          >
            <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </form>

        {importResult && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs" data-testid="voucher-import-result">
            <p className="font-medium text-slate-700">
              Imported {importResult.created_count} code(s){importResult.error_count > 0 ? `, skipped ${importResult.error_count} row(s)` : ''}.
            </p>
            {importResult.errors?.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 text-red-500">
                {importResult.errors.map((e, i) => (
                  <li key={i}>Row {e.row}{e.code ? ` (${e.code})` : ''}: {e.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {analytics.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="voucher-analytics-chart">
            <p className="text-xs font-medium text-slate-600 mb-3 flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5 text-amber-500" /> Redemptions by agent
            </p>
            <ResponsiveContainer width="100%" height={Math.max(120, analytics.length * 36)}>
              <BarChart data={analytics} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="agent_name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value, name) => [value, name === 'redeemed_count' ? 'Redeemed' : 'Total codes']} />
                <Bar dataKey="total_codes" fill="#e2e8f0" name="total_codes" radius={[0, 4, 4, 0]} barSize={14} />
                <Bar dataKey="redeemed_count" fill="#10b981" name="redeemed_count" radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
            {analytics.some((a) => a.current_streak >= 2) && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100" data-testid="voucher-streak-list">
                {analytics.filter((a) => a.current_streak >= 2).map((a) => (
                  <span
                    key={a.agent_username}
                    className="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5"
                    data-testid={`voucher-streak-${a.agent_username}`}
                    title={`${a.current_streak} redemptions in a row`}
                  >
                    <Flame className="w-3 h-3" /> {a.agent_name}: {a.current_streak} in a row
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {vouchers.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No voucher codes yet.</p>
        ) : (
          <div className="space-y-2">
            {vouchers.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid={`voucher-row-${v.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{v.code} <span className="text-slate-400 font-normal">— {v.label}</span></p>
                  <p className="text-xs text-slate-500">
                    Agent: {v.agent_username}
                    {v.expires_at && <span className={isExpired(v) ? 'text-red-500' : ''}> · Expires {v.expires_at}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                      v.redeemed ? 'bg-slate-100 text-slate-500 border-slate-200'
                        : isExpired(v) ? 'bg-red-50 text-red-600 border-red-200'
                        : v.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}
                    data-testid={`voucher-status-${v.id}`}
                  >
                    {v.redeemed ? 'Redeemed' : isExpired(v) ? 'Expired' : v.active ? 'Active' : 'Disabled'}
                  </span>
                  {!v.redeemed && (
                    <button
                      type="button"
                      onClick={() => toggleActive(v)}
                      data-testid={`voucher-toggle-active-${v.id}`}
                      className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                    >
                      {v.active ? 'Disable' : 'Enable'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(v.id)}
                    data-testid={`voucher-delete-${v.id}`}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
};

export default VoucherCodesPanel;
